import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { InputTextarea } from 'primereact/inputtextarea'
import FormField from '../FormField'
import { resolveFieldError } from '../../../lib/forms/messages'

/**
 * RHF-bağlı çok satırlı metin alanı (020). `Controller` + PrimeReact `InputTextarea` + `FormField`.
 */
interface FormTextareaFieldProps<T extends FieldValues> {
  control: Control<T>
  name: FieldPath<T>
  label: string
  required?: boolean
  full?: boolean
  placeholder?: string
  maxLength?: number
  rows?: number
  /**
   * Alanı devre dışı bırakır. Değer RHF'de kalır; temizleme sorumluluğu çağırandadır.
   *
   * <p>Şu an hiçbir çağıran kullanmıyor — 059'da statik içerik alanı bu yolla kapatılıyordu, artık
   * koşul sağlanmadığında **hiç render edilmiyor** (bkz. `TemplateParameterFormDialog`). Prop,
   * `FormTextField` ile aynı yüzeyi taşıması ve "geçici olarak kapalı ama doldurulacak" gerçek
   * durumları desteklemesi için korundu.
   */
  disabled?: boolean
  /**
   * <b>Salt okunur</b> — `disabled`'dan FARKLI ve görüntüleme kipinde doğru olan budur:
   * denetim odaklanılabilir ve metni seçilebilir kalır, yalnız değiştirilemez. `disabled`
   * kontrolü klavye odak sırasından tamamen çıkarır, yani taşan metin okunamaz hâle gelir
   * (THY AI review HIGH bulgusu, 26.10.2026).
   */
  readOnly?: boolean
}

export default function FormTextareaField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  full = true,
  placeholder,
  maxLength,
  rows = 3,
  disabled,
  readOnly,
}: Readonly<FormTextareaFieldProps<T>>) {
  const { t } = useTranslation()
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormField
          label={label}
          htmlFor={name}
          required={required}
          full={full}
          error={resolveFieldError(fieldState.error?.message, t)}
        >
          <InputTextarea
            id={name}
            name={field.name}
            value={field.value ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            rows={rows}
            maxLength={maxLength}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            className={fieldState.error ? 'p-invalid' : undefined}
          />
        </FormField>
      )}
    />
  )
}
